import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  ChevronRight,
  Trophy,
  Target,
  Zap,
  Lock
} from 'lucide-react';

// Sport Major configurations
const SPORT_MAJORS = [
  {
    id: 'basketball',
    name: 'Basketball',
    icon: '🏀',
    color: 'orange',
    gradient: 'from-orange-600 to-amber-500',
    borderColor: 'border-orange-500',
    bgColor: 'bg-orange-500',
    textColor: 'text-orange-500',
    available: true,
    description: 'Master the court with tactical IQ',
    preview: 'The Pick & Roll. The Five-Out. PPS over PPG.',
    modules: ['Historical Blueprint', 'Tactical Theory', 'Film Room Analysis']
  },
  {
    id: 'football',
    name: 'Football',
    icon: '🏈',
    color: 'green',
    gradient: 'from-green-600 to-emerald-500',
    borderColor: 'border-green-500',
    bgColor: 'bg-green-500',
    textColor: 'text-green-500',
    available: false,
    description: 'Dominate the gridiron mentally',
    preview: 'Zone vs Man. RPO reads. Cap space mastery.',
    modules: ['Playbook Foundations', 'Defensive Schemes', 'Contract Strategy']
  },
  {
    id: 'baseball',
    name: 'Baseball',
    icon: '⚾',
    color: 'red',
    gradient: 'from-red-600 to-rose-500',
    borderColor: 'border-red-500',
    bgColor: 'bg-red-500',
    textColor: 'text-red-500',
    available: false,
    description: 'Think like a GM',
    preview: 'Sabermetrics. Launch angle. WAR calculation.',
    modules: ['Analytics Deep Dive', 'Pitch Recognition', 'Front Office IQ']
  },
  {
    id: 'soccer',
    name: 'Soccer',
    icon: '⚽',
    color: 'blue',
    gradient: 'from-blue-600 to-cyan-500',
    borderColor: 'border-blue-500',
    bgColor: 'bg-blue-500',
    textColor: 'text-blue-500',
    available: false,
    description: 'Global game intelligence',
    preview: 'Pressing triggers. xG models. Transfer valuations.',
    modules: ['Tactical Formations', 'Set Piece Strategy', 'Global Market']
  }
];

const SportMajorCard = ({ sport, isSelected, onSelect, onHover, isHovered }) => {
  const isAvailable = sport.available;
  
  return (
    <Card
      className={`relative overflow-hidden cursor-pointer transition-all duration-500 transform ${
        isSelected 
          ? `${sport.borderColor} border-2 scale-105 shadow-2xl shadow-${sport.color}-500/30` 
          : isHovered && isAvailable
          ? `${sport.borderColor} border-2 scale-[1.02] shadow-xl`
          : 'border-gray-800 border-2 hover:border-gray-700'
      } ${!isAvailable ? 'opacity-60' : ''}`}
      onClick={() => isAvailable && onSelect(sport)}
      onMouseEnter={() => isAvailable && onHover(sport.id)}
      onMouseLeave={() => onHover(null)}
    >
      {/* Glow Effect when selected/hovered */}
      {(isSelected || isHovered) && isAvailable && (
        <div className={`absolute inset-0 bg-gradient-to-br ${sport.gradient} opacity-10`}></div>
      )}
      
      {/* Lock overlay for unavailable */}
      {!isAvailable && (
        <div className="absolute inset-0 bg-black/60 z-10 flex items-center justify-center">
          <div className="text-center">
            <Lock className="w-8 h-8 text-gray-500 mx-auto mb-2" />
            <span className="text-gray-500 text-xs font-bold uppercase tracking-wider">Coming Soon</span>
          </div>
        </div>
      )}

      <CardContent className="p-6 relative z-0">
        {/* Sport Icon */}
        <div className={`text-6xl mb-4 transition-transform duration-300 ${
          isSelected || isHovered ? 'scale-110' : ''
        }`}>
          {sport.icon}
        </div>

        {/* Sport Name */}
        <h3 className={`text-2xl font-black uppercase tracking-wider mb-2 transition-colors ${
          isSelected ? sport.textColor : 'text-white'
        }`}>
          {sport.name}
        </h3>

        {/* Description */}
        <p className="text-gray-400 text-sm mb-4">{sport.description}</p>

        {/* Preview Text */}
        <div className={`text-xs font-mono ${sport.textColor} opacity-80 mb-4`}>
          "{sport.preview}"
        </div>

        {/* Modules Preview */}
        <div className="space-y-1">
          {sport.modules.map((module, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <div className={`w-1.5 h-1.5 rounded-full ${sport.bgColor}`}></div>
              <span className="text-gray-500 text-xs">{module}</span>
            </div>
          ))}
        </div>

        {/* Selection Indicator */}
        {isSelected && (
          <div className={`absolute top-4 right-4 ${sport.bgColor} rounded-full p-1`}>
            <Zap className="w-4 h-4 text-black" />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SportMajorSelection = ({ currentMajor, onSelectMajor }) => {
  const navigate = useNavigate();
  const [selectedSport, setSelectedSport] = useState(currentMajor || null);
  const [hoveredSport, setHoveredSport] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleSelect = (sport) => {
    setSelectedSport(sport.id);
    setShowPreview(true);
    if (onSelectMajor) {
      onSelectMajor(sport.id);
    }
  };

  const selectedSportData = SPORT_MAJORS.find(s => s.id === selectedSport);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <Badge className="bg-orange-500/20 text-orange-500 border border-orange-500/50 mb-3">
          CHOOSE YOUR PATH
        </Badge>
        <h2 className="text-3xl font-black text-white uppercase tracking-wider mb-2">
          Select Your <span className="text-orange-500">Sport Major</span>
        </h2>
        <p className="text-gray-500 text-sm">
          Your major customizes Logic Engine questions with sport-specific context
        </p>
      </div>

      {/* Sport Cards Grid - Character Select Style */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {SPORT_MAJORS.map((sport) => (
          <SportMajorCard
            key={sport.id}
            sport={sport}
            isSelected={selectedSport === sport.id}
            onSelect={handleSelect}
            onHover={setHoveredSport}
            isHovered={hoveredSport === sport.id}
          />
        ))}
      </div>

      {/* Scout's Brief Preview Panel */}
      {showPreview && selectedSportData && (
        <Card className={`bg-gradient-to-br from-gray-900 to-black border-2 ${selectedSportData.borderColor} overflow-hidden animate-in slide-in-from-bottom-4 duration-500`}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="text-4xl">{selectedSportData.icon}</div>
                <div>
                  <h3 className="text-xl font-black text-white uppercase">
                    {selectedSportData.name} Major Selected
                  </h3>
                  <p className={`${selectedSportData.textColor} text-sm font-bold`}>
                    Scout's Brief Ready
                  </p>
                </div>
              </div>
              <Button
                onClick={() => navigate(`/academy/sport-major/${selectedSportData.id}`)}
                className={`${selectedSportData.bgColor} hover:opacity-90 text-black font-black`}
                data-testid={`enter-${selectedSportData.id}-major-btn`}
              >
                Enter Module
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {/* Preview Content */}
            <div className="mt-4 pt-4 border-t border-gray-800">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className={`w-4 h-4 ${selectedSportData.textColor}`} />
                    <span className="text-white font-bold text-sm">Scout's Brief</span>
                  </div>
                  <p className="text-gray-400 text-xs">Historical context & tactical foundations</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className={`w-4 h-4 ${selectedSportData.textColor}`} />
                    <span className="text-white font-bold text-sm">Tape Room</span>
                  </div>
                  <p className="text-gray-400 text-xs">Quiz your knowledge with film-study questions</p>
                </div>
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className={`w-4 h-4 ${selectedSportData.textColor}`} />
                    <span className="text-white font-bold text-sm">Logic Engine</span>
                  </div>
                  <p className="text-gray-400 text-xs">Sport-themed math & literacy problems</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SportMajorSelection;
